import { useEffect, useState } from 'react';
import { DeleteOutlined, EditOutlined, VideoCameraOutlined } from '@ant-design/icons';
import { FormInstance, FormItemProps, Progress, UploadProps, message, Upload, Button } from 'antd';
import Form, { Rule } from 'antd/es/form';
import { useFileUpload } from '../../../hooks/api/files';
import VideoDisplay from './VideoDisplay';

import styles from './index.module.scss';

const { Dragger } = Upload;

interface FileProps extends FormItemProps {
  form: FormInstance;
  label?: string;
  name?: string;
  rules?: Rule[];
  displayDescription?: boolean;
  disabled?: boolean;
  defaultFileLocation?: string;
}

export default function InputVideoDragger({
  form,
  name = 'fileId',
  rules = [],
  label,
  displayDescription = true,
  defaultFileLocation,
  disabled,
}: FileProps) {
  const [isIgnoreDefault, setIsIgnoreDefault] = useState(false); // Used to ignore defaultFileLocation

  const [percentCompleted, setPercentCompleted] = useState<number | null>(null);
  const controller = new AbortController();
  // eslint-disable-next-line max-len
  const uploader = useFileUpload(
    (progressEvent) => { setPercentCompleted(Math.round((progressEvent.loaded / (progressEvent?.total || 0)) * 100)); },
    controller,
    'admin/files/upload/video',
  );

  useEffect(() => {
    if (uploader?.data?.id) {
      form.setFieldValue(name, uploader?.data?.id);
    }
  }, [uploader?.data?.id]);

  const videoUrl = uploader?.data?.location || (isIgnoreDefault ? undefined : defaultFileLocation);

  const props: UploadProps = {
    name: 'file',
    multiple: false,
    action: (f) => new Promise(() => {
      uploader.fetchFile(f)
        .finally(() => {
          setPercentCompleted(null);
        });
    }),
    onChange(info) {
      const { status } = info.file;

      if (status !== 'uploading') {
        console.log(info.file, info.fileList);
      }
      if (status === 'done') {
        message.success(`${info.file.name} file uploaded successfully.`);
      } else if (status === 'error') {
        message.error(`${info.file.name} file upload failed.`);
      }
    },
    onDrop(e) {
      console.log('Dropped files', e.dataTransfer.files);
    },
  };

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    // Abort ongoing upload if any
    controller.abort();
    uploader.clearResponse();
    form.setFieldValue(name, null);
    // Clear internal AntD Upload file list (optional)
    form.validateFields([name]); // to trigger validation

    setIsIgnoreDefault(true); // Change state, to ignore 'default file' prop after deletion click
  };

  return (
    <Form.Item name={name} rules={rules} label={label}>
      <Dragger {...props} accept="video/*" disabled={disabled || uploader?.loading} className={styles.dragger}>
        {uploader?.loading || !videoUrl ? (
          <div>
            <p className="ant-upload-drag-icon">
              <VideoCameraOutlined />
            </p>
            <p className="ant-upload-text">Click or drag file to this area to upload Video</p>
            {displayDescription ? (
              <p className="ant-upload-hint">
                Support for a single upload. Strictly prohibited from uploading company data or other banned
                files.
              </p>
            ) : null}
          </div>
        ) : (
          <>
            <VideoDisplay src={videoUrl} />
            <div className={styles.actions}>
              <Button type="text" danger icon={<DeleteOutlined />} onClick={handleDelete} />
              <Button type="text" icon={<EditOutlined />} />
            </div>
          </>
        )}
        {percentCompleted ? (<Progress showInfo={false} percent={percentCompleted} style={{ width: '100%' }} />) : null}
      </Dragger>
    </Form.Item>
  );
}
